import React, { useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Avatar,
  Divider,
  Paper,
  SelectChangeEvent,
  Fade,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Add,
  Event,
  Schedule,
  Campaign,
  ContentCopy,
  Edit,
  Delete,
  CheckCircle,
  Today,
  ViewWeek,
  ViewModule,
  ArrowBack,
  ArrowForward,
  MoreHoriz,
  AccessTime,
  Person,
  Business,
} from '@mui/icons-material';
import { brandColors } from '../../theme';
import PostPreview, { ContentPost } from '../../components/ContentPool/PostPreview';

interface CalendarEvent {
  id: number;
  title: string;
  type: 'campaign' | 'content' | 'review' | 'meeting';
  date: string;
  time: string;
  duration: string;
  status: 'scheduled' | 'in-progress' | 'completed' | 'cancelled';
  campaign?: string;
  assignee: string;
  description?: string;
}

interface ViewType {
  month: number;
  year: number;
}

const CalendarPlanning: React.FC = () => {
  const [view, setView] = useState<'month' | 'week' | 'day'>('month');
  const [currentDate, setCurrentDate] = useState<ViewType>({ month: 8, year: 2025 }); // September 2025
  const [newEventOpen, setNewEventOpen] = useState(false);
  const [contentPoolOpen, setContentPoolOpen] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPost, setPreviewPost] = useState<ContentPost | null>(null);
  const [pendingDropDate, setPendingDropDate] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [newEventData, setNewEventData] = useState({
    title: '',
    type: '',
    date: '',
    time: '',
    campaign: '',
    assignee: '',
    description: '',
  });

  const events: CalendarEvent[] = [
    {
      id: 1,
      title: 'Kampagnen Review Meeting',
      type: 'meeting',
      date: '2025-09-16',
      time: '10:00',
      duration: '1h',
      status: 'scheduled',
      campaign: 'Sommer Kampagne',
      assignee: 'Maria Schmidt',
      description: 'Quarterly review of all active campaigns'
    },
    {
      id: 2,
      title: 'Content-Erstellung: Herbst Banner',
      type: 'content',
      date: '2025-09-17',
      time: '09:00',
      duration: '3h',
      status: 'scheduled',
      campaign: 'Herbst Promotion',
      assignee: 'Thomas Müller',
      description: 'Design and create autumn promotional banners'
    },
    {
      id: 3,
      title: 'Weihnachts Kampagne Start',
      type: 'campaign',
      date: '2025-09-20',
      time: '08:00',
      duration: 'Ganztägig',
      status: 'scheduled',
      campaign: 'Weihnachts Special',
      assignee: 'Anna Weber',
      description: 'Launch of Christmas special campaign'
    },
    {
      id: 4,
      title: 'Content Review & Freigabe',
      type: 'review',
      date: '2025-09-18',
      time: '14:00',
      duration: '2h',
      status: 'scheduled',
      campaign: 'Herbst Promotion',
      assignee: 'Lisa Johnson',
      description: 'Review and approve new content pieces'
    },
    {
      id: 5,
      title: 'Performance Analytics Review',
      type: 'meeting',
      date: '2025-09-19',
      time: '15:30',
      duration: '1.5h',
      status: 'scheduled',
      assignee: 'Michael Brown',
      description: 'Weekly performance analysis meeting'
    },
  ];

  const contentPoolItems: ContentPost[] = [
    {
      id: 'cp1',
      title: 'IG Reel Herbstaktion',
      text: '🍂 Herbst-Deal jetzt sichern! #angebot #herbst',
      platforms: ['instagram'],
      media: [
        { id: 'm1', type: 'video', src: 'https://www.w3schools.com/html/mov_bbb.mp4' },
      ],
    },
    {
      id: 'cp2',
      title: 'FB Bildposting',
      text: 'Jetzt neu im Sortiment! ✅',
      platforms: ['facebook'],
      media: [
        { id: 'm2', type: 'image', src: 'https://via.placeholder.com/600x400.png?text=Produkt' },
      ],
    },
  ];

  const handleDragStart = (post: ContentPost) => (e: React.DragEvent<HTMLDivElement>) => {
    e.dataTransfer.setData('application/x-post', JSON.stringify(post));
    e.dataTransfer.effectAllowed = 'copyMove';
  };

  const handleDayDragOver = (e: React.DragEvent) => {
    if (e.dataTransfer.types.includes('application/x-post')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [schedulePost, setSchedulePost] = useState<ContentPost | null>(null);

  const openScheduleForDrop = (dateStr: string, post: ContentPost) => {
    setPendingDropDate(dateStr);
    setSchedulePost(post);
    setScheduleDialogOpen(true);
  };

  const handleDayDrop = (dateStr: string) => (e: React.DragEvent) => {
    const data = e.dataTransfer.getData('application/x-post');
    if (!data) return;
    const post: ContentPost = JSON.parse(data);
    openScheduleForDrop(dateStr, post);
  };

  const getEventTypeColor = (type: string) => {
    switch (type) {
      case 'campaign': return 'var(--brand)';
      case 'content': return 'var(--brand)';
      case 'review': return 'var(--warning)';
      case 'meeting': return 'var(--slate-600)';
      default: return 'var(--slate-600)';
    }
  };

  const getEventTypeIcon = (type: string) => {
    switch (type) {
      case 'campaign': return <Campaign />;
      case 'content': return <ContentCopy />;
      case 'review': return <CheckCircle />;
      case 'meeting': return <Event />;
      default: return <Schedule />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'var(--success)';
      case 'in-progress': return 'var(--brand)';
      case 'scheduled': return 'var(--warning)';
      case 'cancelled': return 'var(--danger)';
      default: return 'var(--slate-600)';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return 'Abgeschlossen';
      case 'in-progress': return 'In Bearbeitung';
      case 'scheduled': return 'Geplant';
      case 'cancelled': return 'Storniert';
      default: return 'Unbekannt';
    }
  };

  const handleNewEventOpen = () => {
    setNewEventOpen(true);
  };

  const handleNewEventClose = () => {
    setNewEventOpen(false);
    setNewEventData({
      title: '',
      type: '',
      date: '',
      time: '',
      campaign: '',
      assignee: '',
      description: '',
    });
  };

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setNewEventData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSelectChange = (field: string) => (event: SelectChangeEvent) => {
    setNewEventData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event);
  };

  const handleEventClose = () => {
    setSelectedEvent(null);
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      if (direction === 'next') {
        return prev.month === 11 
          ? { month: 0, year: prev.year + 1 }
          : { month: prev.month + 1, year: prev.year };
      } else {
        return prev.month === 0 
          ? { month: 11, year: prev.year - 1 }
          : { month: prev.month - 1, year: prev.year };
      }
    });
  };

  const monthNames = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];

  const getDaysInMonth = (month: number, year: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month: number, year: number) => {
    return new Date(year, month, 1).getDay();
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate.month, currentDate.year);
    const firstDay = getFirstDayOfMonth(currentDate.month, currentDate.year);
    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <Grid key={`empty-${i}`} size={12/7}>
          <Box sx={{ height: { xs: 100, sm: 120, md: 140 }, minHeight: { xs: 100, sm: 120, md: 140 } }} />
        </Grid>
      );
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${currentDate.year}-${String(currentDate.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayEvents = events.filter(event => event.date === dateStr);
      const isToday = dateStr === '2025-09-16'; // Mock today's date
      const isWeekend = (firstDay + day - 1) % 7 === 0 || (firstDay + day - 1) % 7 === 6;

      days.push(
        <Grid key={day} size={12/7}>
          <Fade in timeout={300 + (day * 50)}>
            <Paper 
              elevation={0}
              sx={{ 
                height: { xs: 100, sm: 120, md: 140 },
                minHeight: { xs: 100, sm: 120, md: 140 },
                p: { xs: 1, sm: 1.2, md: 1.5 }, 
                border: '1px solid var(--line)',
                background: isWeekend ? 'var(--slate-50)' : 'var(--card)',
                cursor: 'pointer',
                position: 'relative',
                transition: 'transform 180ms cubic-bezier(0.2,0,0,1), box-shadow 180ms',
                boxShadow: isToday ? 'inset 0 0 0 2px var(--accent)' : 'none',
                '&:hover': { 
                  boxShadow: 'var(--shadow-sm)',
                  transform: 'translateY(-2px)'
                }
              }}
              onDragOver={handleDayDragOver}
              onDrop={handleDayDrop(dateStr)}
            >
              {/* Day number with modern styling */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 1.5
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: isToday ? 700 : 600, 
                    fontSize: '1.05rem',
                    color: 'var(--ink)'
                  }}
                >
                  {day}
                </Typography>
                {isToday && (
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'var(--brand)', animation: 'pulse 2s infinite' }} />
                )}
              </Box>

              {/* Events with improved styling */}
              <Box sx={{ maxHeight: 80, overflow: 'hidden' }}>
                {dayEvents.slice(0, 3).map((event, index) => (
                  <Fade in timeout={400 + (index * 100)} key={event.id}>
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEventClick(event);
                      }}
                      sx={{
                        p: 0.8,
                        mb: 0.6,
                        bgcolor: 'var(--slate-50)',
                        border: '1px solid var(--line)',
                        borderRadius: 2,
                        cursor: 'pointer',
                        position: 'relative',
                        transition: 'transform 160ms cubic-bezier(0.2,0,0,1), background 160ms',
                        '&:hover': { bgcolor: 'var(--slate-100)', transform: 'translateX(2px)' },
                        '&::before': { content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, bgcolor: getEventTypeColor(event.type), borderRadius: '0 2px 2px 0' }
                      }}
                    >
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'var(--ink)',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          display: 'block',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          pl: 1
                        }}
                      >
                        {event.title}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'var(--muted)',
                          fontSize: '0.65rem',
                          display: 'block',
                          pl: 1
                        }}
                      >
                        {event.time}
                      </Typography>
                    </Box>
                  </Fade>
                ))}
                {dayEvents.length > 3 && (
                  <Tooltip title={`${dayEvents.length - 3} weitere Termine`}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mt: 0.5,
                      p: 0.5,
                      bgcolor: brandColors.midnightSteel + '10',
                      borderRadius: 1,
                      cursor: 'pointer'
                    }}>
                      <MoreHoriz sx={{ fontSize: 16, color: brandColors.midnightSteel }} />
                      <Typography variant="caption" sx={{ 
                        color: brandColors.midnightSteel,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        ml: 0.5
                      }}>
                        +{dayEvents.length - 3}
                      </Typography>
                    </Box>
                  </Tooltip>
                )}
              </Box>
            </Paper>
          </Fade>
        </Grid>
      );
    }

    return days;
  };

  return (
    <Box>
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.7; transform: scale(1.1); }
            100% { opacity: 1; transform: scale(1); }
          }
          .calendar-day-text {
            color: #00324D !important;
          }
          .MuiTypography-h6.calendar-day-text {
            color: #00324D !important;
          }
          .MuiTypography-root.MuiTypography-h6.calendar-day-text {
            color: #00324D !important;
          }
          .calendar-event-text {
            color: #00324D !important;
          }
        `}
      </style>
      {/* Header Section (neutral) */}
      <Box sx={{ mb: 4, p: 3, borderRadius: 'var(--radius)', border: '1px solid var(--line)', backgroundColor: 'var(--card)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h2" sx={{ fontWeight: 600, mb: 1 }}>
              Kalender Planung
            </Typography>
            <Typography variant="body1" sx={{ color: 'var(--muted)' }}>
              Planen und verwalten Sie alle Termine und Deadlines
            </Typography>
          </Box>
          <Button variant="contained" size="large" startIcon={<Add />} onClick={handleNewEventOpen}>
            Neuer Termin
          </Button>
        </Box>
      </Box>
      {/* Calendar Controls */}
      <Card sx={{ mb: 4, borderRadius: 3, overflow: 'hidden' }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            flexWrap: 'wrap', 
            gap: 2,
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: { xs: 1, sm: 2 }, 
              flexWrap: 'wrap',
              justifyContent: { xs: 'center', sm: 'flex-start' }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title="Vorheriger Monat">
                  <IconButton 
                    onClick={() => navigateMonth('prev')}
                    sx={{ 
                      bgcolor: brandColors.electricTeal + '10',
                      '&:hover': { bgcolor: brandColors.electricTeal + '20' }
                    }}
                  >
                    <ArrowBack />
                  </IconButton>
                </Tooltip>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700, 
                    minWidth: { xs: 200, sm: 250 }, 
                    textAlign: 'center',
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
                    background: `linear-gradient(135deg, ${brandColors.electricTeal} 0%, ${brandColors.midnightSteel} 100%)`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  {monthNames[currentDate.month]} {currentDate.year}
                </Typography>
                <Tooltip title="Nächster Monat">
                  <IconButton 
                    onClick={() => navigateMonth('next')}
                    sx={{ 
                      bgcolor: brandColors.electricTeal + '10',
                      '&:hover': { bgcolor: brandColors.electricTeal + '20' }
                    }}
                  >
                    <ArrowForward />
                  </IconButton>
                </Tooltip>
              </Box>
              <Button
                variant="outlined"
                startIcon={<Today />}
                onClick={() => setCurrentDate({ month: 8, year: 2025 })}
                sx={{
                  borderColor: brandColors.electricTeal,
                  color: brandColors.midnightSteel,
                  bgcolor: brandColors.electricTeal + '10',
                  '&:hover': {
                    bgcolor: brandColors.electricTeal + '20',
                    borderColor: brandColors.electricTeal,
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                Heute
              </Button>
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              bgcolor: brandColors.midnightSteel + '05', 
              p: 1, 
              borderRadius: 2,
              justifyContent: { xs: 'center', sm: 'flex-end' }
            }}>
              <Tooltip title="Monatsansicht">
                <IconButton
                  color={view === 'month' ? 'primary' : 'default'}
                  onClick={() => setView('month')}
                  sx={{
                    bgcolor: view === 'month' ? brandColors.electricTeal : 'transparent',
                    color: view === 'month' ? brandColors.clearLight : brandColors.midnightSteel,
                    '&:hover': { 
                      bgcolor: view === 'month' ? brandColors.electricTeal : brandColors.electricTeal + '20'
                    }
                  }}
                >
                  <ViewModule />
                </IconButton>
              </Tooltip>
              <Tooltip title="Wochenansicht">
                <IconButton
                  color={view === 'week' ? 'primary' : 'default'}
                  onClick={() => setView('week')}
                  sx={{
                    bgcolor: view === 'week' ? brandColors.electricTeal : 'transparent',
                    color: view === 'week' ? brandColors.clearLight : brandColors.midnightSteel,
                    '&:hover': { 
                      bgcolor: view === 'week' ? brandColors.electricTeal : brandColors.electricTeal + '20'
                    }
                  }}
                >
                  <ViewWeek />
                </IconButton>
              </Tooltip>
              <Tooltip title="Tagesansicht">
                <IconButton
                  color={view === 'day' ? 'primary' : 'default'}
                  onClick={() => setView('day')}
                  sx={{
                    bgcolor: view === 'day' ? brandColors.electricTeal : 'transparent',
                    color: view === 'day' ? brandColors.clearLight : brandColors.midnightSteel,
                    '&:hover': { 
                      bgcolor: view === 'day' ? brandColors.electricTeal : brandColors.electricTeal + '20'
                    }
                  }}
                >
                  <Today />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
      </Card>
      {/* Content Pool (moved below controls) */}
      <Card sx={{ mb: 4, borderRadius: 'var(--radius)', overflow: 'hidden', border: '1px solid var(--line)' }}>
        <Box sx={{ background: 'var(--slate-50)', p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--line)' }}>
          <Typography variant="h6" sx={{ color: 'var(--ink)', fontWeight: 700 }}>Content Pool</Typography>
          <Button variant="outlined" onClick={() => setContentPoolOpen(v => !v)}>
            {contentPoolOpen ? 'Einklappen' : 'Ausklappen'}
          </Button>
        </Box>
        {contentPoolOpen && (
          <CardContent>
            <Grid container spacing={2}>
              {contentPoolItems.map((item) => (
                <Grid key={item.id} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Box
                    draggable
                    onDragStart={handleDragStart(item)}
                    onClick={() => { setPreviewPost(item); setPreviewOpen(true); }}
                    sx={{
                      p: 2,
                      borderRadius: 'var(--radius)',
                      border: '1px solid var(--line)',
                      background: 'var(--card)',
                      cursor: 'grab',
                      transition: 'transform 160ms cubic-bezier(0.2,0,0,1), box-shadow 160ms',
                      '&:hover': { boxShadow: 'var(--shadow-sm)', transform: 'translateY(-2px)' },
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>{item.title}</Typography>
                    <Typography variant="body2" sx={{ mb: 1, color: 'var(--muted)' }} noWrap>{item.text}</Typography>
                    <Stack direction="row" spacing={1}>
                      {item.platforms.map(p => (
                        <Chip key={p} label={p} size="small" sx={{ border: '1px solid var(--line)' }} />
                      ))}
                    </Stack>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        )}
      </Card>
      <Grid container spacing={{ xs: 2, md: 3 }}>
        {/* Calendar Grid */}
        <Grid
          size={{
            xs: 12,
            lg: 9
          }}>
      <Card sx={{ borderRadius: 'var(--radius)', overflow: 'hidden', boxShadow: 'var(--shadow-md)', border: '1px solid var(--line)' }}>
        <CardContent sx={{ p: 0 }}>
              {/* Calendar Header */}
              <Box sx={{ background: 'var(--slate-50)', p: 2, borderBottom: '1px solid var(--line)' }}>
                <Grid container spacing={0}>
                  {['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'].map((day, index) => (
                    <Grid key={day} size={12/7}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 1.5,
                        px: 1,
                        borderRadius: 1
                      }}>
                        <Typography 
                          variant="subtitle1" 
                          align="center" 
                          sx={{ 
                            fontWeight: 700, 
                            color: 'var(--muted)',
                            fontSize: '0.9rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}
                        >
                          {day}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
              
              {/* Calendar Days */}
              <Box sx={{ p: 2 }}>
                <Grid container spacing={1}>
                  {renderCalendarDays()}
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid
          size={{
            xs: 12,
            lg: 3
          }}>
          <Stack spacing={3}>
            {/* Upcoming Events */}
            <Card sx={{ borderRadius: 'var(--radius)', overflow: 'hidden', border: '1px solid var(--line)' }}>
              <Box sx={{ 
                background: 'var(--slate-50)',
                p: 2,
                borderBottom: '1px solid var(--line)'
              }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 700,
                  color: 'var(--ink)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <Schedule />
                  Kommende Termine
                </Typography>
              </Box>
              <CardContent sx={{ p: 0 }}>
                <List sx={{ p: 0 }}>
                  {events.slice(0, 4).map((event, index) => (
                    <Fade in timeout={500 + (index * 100)} key={event.id}>
                      <ListItem 
                        sx={{ 
                          px: 2, 
                          py: 1.5,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            bgcolor: brandColors.electricTeal + '05',
                            transform: 'translateX(4px)'
                          }
                        }} 
                        onClick={() => handleEventClick(event)}
                      >
                        <ListItemIcon>
                          <Avatar sx={{ 
                            bgcolor: getEventTypeColor(event.type), 
                            width: 40, 
                            height: 40,
                            boxShadow: `0 4px 12px ${getEventTypeColor(event.type)}40`
                          }}>
                            {React.cloneElement(getEventTypeIcon(event.type), { sx: { fontSize: 18 } })}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="subtitle2" sx={{ 
                              fontWeight: 600,
                              color: brandColors.midnightSteel,
                              mb: 0.5
                            }}>
                              {event.title}
                            </Typography>
                          }
                          secondary={
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <AccessTime sx={{ fontSize: 14, color: brandColors.electricTeal }} />
                                <Typography variant="caption" sx={{ color: brandColors.electricTeal, fontWeight: 500 }}>
                                  {event.date} • {event.time}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Person sx={{ fontSize: 14, color: brandColors.midnightSteel + '80' }} />
                                <Typography variant="caption" sx={{ color: brandColors.midnightSteel + '80' }}>
                                  {event.assignee}
                                </Typography>
                              </Box>
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Chip 
                            label={getStatusText(event.status)} 
                            size="small"
                            sx={{ 
                              bgcolor: `${getStatusColor(event.status)}15`,
                              color: getStatusColor(event.status),
                              border: `1px solid ${getStatusColor(event.status)}30`,
                              fontSize: '0.7rem',
                              fontWeight: 600
                            }}
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    </Fade>
                  ))}
                </List>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card sx={{ borderRadius: 'var(--radius)', overflow: 'hidden', border: '1px solid var(--line)' }}>
              <Box sx={{ 
                background: 'var(--slate-50)',
                p: 2,
                borderBottom: '1px solid var(--line)'
              }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 700,
                  color: 'var(--ink)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <Business />
                  Quick Actions
                </Typography>
              </Box>
              <CardContent>
                <Stack spacing={2}>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    startIcon={<Campaign />}
                    sx={{ 
                      justifyContent: 'flex-start', 
                      textAlign: 'left',
                      py: 1.5,
                      borderColor: brandColors.electricTeal,
                      color: brandColors.midnightSteel,
                      '&:hover': {
                        bgcolor: brandColors.electricTeal + '10',
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${brandColors.electricTeal}30`
                      }
                    }}
                  >
                    Kampagne planen
                  </Button>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    startIcon={<ContentCopy />}
                    sx={{ 
                      justifyContent: 'flex-start', 
                      textAlign: 'left',
                      py: 1.5,
                      borderColor: brandColors.electricTeal,
                      color: brandColors.midnightSteel,
                      '&:hover': {
                        bgcolor: brandColors.electricTeal + '10',
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${brandColors.electricTeal}30`
                      }
                    }}
                  >
                    Content Deadline
                  </Button>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    startIcon={<CheckCircle />}
                    sx={{ 
                      justifyContent: 'flex-start', 
                      textAlign: 'left',
                      py: 1.5,
                      borderColor: '#ff9800',
                      color: brandColors.midnightSteel,
                      '&:hover': {
                        bgcolor: '#ff980010',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px #ff980030'
                      }
                    }}
                  >
                    Review Termin
                  </Button>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    startIcon={<Event />}
                    sx={{ 
                      justifyContent: 'flex-start', 
                      textAlign: 'left',
                      py: 1.5,
                      borderColor: brandColors.midnightSteel,
                      color: brandColors.midnightSteel,
                      '&:hover': {
                        bgcolor: brandColors.midnightSteel + '10',
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${brandColors.midnightSteel}30`
                      }
                    }}
                  >
                    Team Meeting
                  </Button>
                </Stack>
              </CardContent>
            </Card>

            {/* Event Type Legend */}
            <Card sx={{ borderRadius: 'var(--radius)', overflow: 'hidden', border: '1px solid var(--line)' }}>
              <Box sx={{ background: 'var(--slate-50)', p: 2, borderBottom: '1px solid var(--line)' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Event />
                  Legende
                </Typography>
              </Box>
              <CardContent>
                <Stack spacing={1.5}>
                  {[
                    { type: 'campaign', label: 'Kampagne', icon: <Campaign />, color: 'var(--brand)' },
                    { type: 'content', label: 'Content', icon: <ContentCopy />, color: 'var(--brand)' },
                    { type: 'review', label: 'Review', icon: <CheckCircle />, color: 'var(--warning)' },
                    { type: 'meeting', label: 'Meeting', icon: <Event />, color: 'var(--slate-600)' }
                  ].map((item) => (
                    <Box key={item.type} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.color }} />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {React.cloneElement(item.icon, { sx: { fontSize: 16, color: item.color } })}
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--ink)' }}>
                          {item.label}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
      {/* New Event Dialog */}
      <Dialog open={newEventOpen} onClose={handleNewEventClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: brandColors.electricTeal, color: brandColors.clearLight }}>
          Neuen Termin erstellen
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Titel"
                value={newEventData.title}
                onChange={handleInputChange('title')}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <FormControl fullWidth>
                <InputLabel>Typ</InputLabel>
                <Select
                  value={newEventData.type}
                  onChange={handleSelectChange('type')}
                  label="Typ"
                >
                  <MenuItem value="campaign">Kampagne</MenuItem>
                  <MenuItem value="content">Content Erstellung</MenuItem>
                  <MenuItem value="review">Review & Freigabe</MenuItem>
                  <MenuItem value="meeting">Meeting</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <FormControl fullWidth>
                <InputLabel>Kampagne</InputLabel>
                <Select
                  value={newEventData.campaign}
                  onChange={handleSelectChange('campaign')}
                  label="Kampagne"
                >
                  <MenuItem value="sommer">Sommer Handwerker Kampagne</MenuItem>
                  <MenuItem value="herbst">Herbst Promotion 2025</MenuItem>
                  <MenuItem value="weihnachten">Weihnachts Special</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                fullWidth
                label="Datum"
                type="date"
                value={newEventData.date}
                onChange={handleInputChange('date')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                fullWidth
                label="Uhrzeit"
                type="time"
                value={newEventData.time}
                onChange={handleInputChange('time')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Zugewiesen an"
                value={newEventData.assignee}
                onChange={handleInputChange('assignee')}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Beschreibung"
                multiline
                rows={3}
                value={newEventData.description}
                onChange={handleInputChange('description')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleNewEventClose}>Abbrechen</Button>
          <Button 
            variant="contained" 
            onClick={handleNewEventClose}
            sx={{ 
              bgcolor: brandColors.electricTeal,
              '&:hover': { bgcolor: brandColors.midnightSteel }
            }}
          >
            Termin erstellen
          </Button>
        </DialogActions>
      </Dialog>
      {/* Schedule Post Dialog */}
      <Dialog open={scheduleDialogOpen} onClose={() => setScheduleDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Zeit für Planung wählen</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Uhrzeit"
              type="time"
              value={scheduleTime}
              onChange={(e) => setScheduleTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Typography variant="body2" color="text.secondary">
              {pendingDropDate ? `Geplanter Tag: ${pendingDropDate}` : ''}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setScheduleDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={() => {
              // here you would persist the scheduled post using schedulePost, pendingDropDate, and scheduleTime
              setScheduleDialogOpen(false);
            }}
            sx={{ bgcolor: brandColors.electricTeal }}
          >
            Planen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Post Preview Modal */}
      <PostPreview open={previewOpen} post={previewPost} onClose={() => setPreviewOpen(false)} />
      {/* Event Details Dialog */}
      <Dialog open={Boolean(selectedEvent)} onClose={handleEventClose} maxWidth="sm" fullWidth>
        {selectedEvent && (
          <>
            <DialogTitle sx={{ 
              bgcolor: getEventTypeColor(selectedEvent.type), 
              color: brandColors.clearLight,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              {getEventTypeIcon(selectedEvent.type)}
              {selectedEvent.title}
            </DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Details
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {selectedEvent.description}
                </Typography>
                
                <Divider sx={{ my: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid size={6}>
                    <Typography variant="caption" color="text.secondary">Datum</Typography>
                    <Typography variant="body2">{selectedEvent.date}</Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" color="text.secondary">Uhrzeit</Typography>
                    <Typography variant="body2">{selectedEvent.time} ({selectedEvent.duration})</Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" color="text.secondary">Zugewiesen an</Typography>
                    <Typography variant="body2">{selectedEvent.assignee}</Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Chip 
                      label={getStatusText(selectedEvent.status)} 
                      size="small"
                      sx={{ 
                        bgcolor: `${getStatusColor(selectedEvent.status)}20`,
                        color: getStatusColor(selectedEvent.status),
                        mt: 0.5
                      }}
                    />
                  </Grid>
                  {selectedEvent.campaign && (
                    <Grid size={12}>
                      <Typography variant="caption" color="text.secondary">Kampagne</Typography>
                      <Typography variant="body2">{selectedEvent.campaign}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button onClick={handleEventClose}>Schließen</Button>
              <Button startIcon={<Edit />}>Bearbeiten</Button>
              <Button startIcon={<Delete />} color="error">Löschen</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default CalendarPlanning;
